<template>
  <div>
    <div class="d-flex justify-content-center" v-if="loading">
      <CRow
        class="
          d-flex
          justify-content-start
          row-cols-1
          row-cols-sm-2
          row-cols-md-2
          row-cols-lg-3
          row-cols-xl-4
          row-cols-xxl-6
          w-100
        "
        v-if="products.length > 0"
      >
        <CCol class="mb-4" v-for="i in queryFilters.limit.term" :key="i">
          <MarketProductCardSkeleton />
        </CCol>
      </CRow>
    </div>
    <div v-else>
      <CRow
        class="
          d-flex
          justify-content-start
          row-cols-1
          row-cols-sm-2
          row-cols-md-2
          row-cols-lg-3
          row-cols-xl-4
          row-cols-xxl-6
          w-100
        "
        v-if="products.length > 0"
      >
        <CCol class="mb-4" v-for="(product, index) in products" :key="index">
          <MarketProductCard v-bind="product" @onClick="onCardClick" />
        </CCol>
      </CRow>
      <div v-if="products.length === 0" class="w-100 pb-4 text-center">
        {{ $t("role.orders.rounds.ddmp.noResults") }}
      </div>
      <Pagination
        v-if="products.length > 0"
        :data="pagination"
        @paginate="loadProducts"
        :maxVisiblePages="7"
      />
    </div>
  </div>
</template>

<script>
import MarketProductCard from "./MarketProductCard";
import { getProductCardDataFromMarketProduct } from "../../../helpers/common";
import { mapGetters } from "vuex";
import { getPriceUnitOptions } from "../../../helpers/options";
import { apiGetRQL } from "../../../helpers/apiHelper";
import Pagination from "../../../common/table/Pagination.vue";
import MarketProductCardSkeleton from "./MarketProductCardSkeleton.vue";

export default {
  name: "MarketShowcaseGrid",

  components: {
    MarketProductCard,
    Pagination,
    MarketProductCardSkeleton,
  },

  props: {
    filter: {
      type: Object,
      default: () => {
        return {};
      },
    },
    bus: { type: Object },
  },

  data: function () {
    return {
      loading: false,
      prods: [],
      numPages: 1,
      currentPage: 1,
      queryFilters: {
        limit: {
          style: "query",
          operator: "=",
          term: 12,
        },
        offset: {
          style: "query",
          operator: "=",
          term: 0,
        },
      },
      pagination: {
        count: 0,
        next: null,
        previous: null,
        limit: 12,
      },
    };
  },

  mounted() {
    const self = this;

    this.initializeFromQuery();

    this.loadProducts(this.currentPage);
    this.bus.$on("onSearch", (filter) => {
      // Reset to page 1 when searching
      self.currentPage = 1;
      self.updateUrl();
      self.loadProducts(1);
    });
  },

  beforeDestroy() {
    this.bus.$off("onSearch");
  },

  computed: {
    ...mapGetters("connections", ["currencySymbol"]),

    products() {
      const lang = this.$i18n; //.locale;
      const currencySymbol = this.currencySymbol;
      const unitOptions = getPriceUnitOptions(this.$i18n);

      return this.prods.map((product) => {
        return getProductCardDataFromMarketProduct(
          product,
          lang,
          currencySymbol,
          unitOptions
        );
      });
    },
  },

  watch: {
    currentPage: function () {
      this.loadProducts(this.currentPage);
    },
  },

  methods: {
    async loadProducts(page = null) {
      this.loading = true;
      const marketId = this.$store.state.shop.market.market.id;
      const familyId = this.$store.state.connections.current.family.id;

      // Update currentPage and URL before making the API call
      if (page) {
        this.currentPage = page;
      }
      this.updateUrl();

      let rqlFilter = this.queryFilters;
      const { search, categoryId, producerId, certificationsIds } = this.filter;

      if (search) {
        rqlFilter["search"] = {
          style: "query",
          term: search,
          operator: "=",
        };
      } else {
        delete rqlFilter["search"];
      }

      if (categoryId) {
        rqlFilter["category"] = {
          style: "query",
          term: categoryId,
          operator: "=",
        };
      } else {
        delete rqlFilter["category"];
      }

      if (producerId) {
        rqlFilter["producer"] = {
          style: "query",
          term: producerId,
          operator: "=",
        };
      } else {
        delete rqlFilter["producer"];
      }

      if (certificationsIds.length > 0) {
        let certIds = [];
        certificationsIds.map((cert) => {
          certIds.push(cert.value);
        });

        if (certIds.length > 1) {
          let certIdsString = "";
          certIds.forEach((id, index) => {
            certIdsString += `eq(certification,${id})`;
            if (index < certIds.length - 1) {
              certIdsString += ",";
            }
          });

          rqlFilter["certification"] = {
            style: "multiFunction",
            term: certIdsString,
            operator: "or",
          };
        }

        if (certIds.length === 1) {
          rqlFilter["certification"] = {
            style: "query",
            term: certIds[0],
            operator: "=",
          };
        }
      } else {
        delete rqlFilter["certification"];
      }

      if (page) {
        rqlFilter.offset = {
          style: "query",
          operator: "=",
          term: (page - 1) * this.queryFilters.limit.term,
        };
      }

      rqlFilter.limit = {
        style: "query",
        operator: "=",
        term: this.queryFilters.limit.term,
      };

      let params = {
        endpoint: "family_market_products",
        params: [familyId, marketId],
        filter: rqlFilter,
      };

      const response = await apiGetRQL(params);

      if (response && response.data) {
        this.numPages = Math.ceil(
          response.data.payload.Count / this.queryFilters.limit.term
        );

        this.prods = response.data.payload.Items;

        this.pagination = {
          count: response.data.payload.Count,
          limit: parseInt(rqlFilter.limit.term),
          offset: parseInt(rqlFilter.offset.term),
        };

        if (!page) {
          this.currentPage =
            Math.floor(
              parseInt(rqlFilter.offset.term) / parseInt(rqlFilter.limit.term)
            ) + 1;
        }

        this.loading = false;
      }
    },

    onCardClick(productId) {
      const familyId = this.$route.params.familyId;
      const marketId = this.$route.params.marketId;
      this.$router.push({
        name: "UserMarketProduct",
        params: { familyId, marketId, productId },
      });
    },

    initializeFromQuery() {
      const query = this.$route.query;

      // Restore page
      if (query.page) {
        this.currentPage = parseInt(query.page) || 1;
      }

      // Restore filters from URL
      if (query.search) {
        this.filter.search = query.search;
      }
      if (query.category) {
        this.filter.categoryId = parseInt(query.category);
      }
      if (query.producer) {
        this.filter.producerId = parseInt(query.producer);
      }

      // Calculate offset from page and limit
      this.queryFilters.offset.term =
        (this.currentPage - 1) * this.queryFilters.limit.term;
    },

    updateUrl() {
      const query = {
        ...this.$route.query,
      };

      // Add/update page if not 1
      if (this.currentPage > 1) {
        query.page = this.currentPage.toString();
      } else {
        delete query.page;
      }

      // Add/update search filter
      if (this.filter.search) {
        query.search = this.filter.search;
      } else {
        delete query.search;
      }

      // Add/update category filter
      if (this.filter.categoryId) {
        query.category = this.filter.categoryId.toString();
      } else {
        delete query.category;
      }

      // Add/update producer filter
      if (this.filter.producerId) {
        query.producer = this.filter.producerId.toString();
      } else {
        delete query.producer;
      }

      // Only update if query actually changed
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
  },
};
</script>
